import { EventType } from "./event-type";
import { WebEvent, getWebEvents } from "./web-event";

async function getUpcomingChallenges(
  maxEvents: number,
): Promise<WebEvent[] | null> {
  const events = await getWebEvents();
  if (events === null) {
    console.error("Failed to load web events. Returning null.");
    return null;
  }

  return (
    events
      .filter((event) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return event.type == EventType.Challenge && event.endTime >= today;
      })
      .slice(0, maxEvents) || []
  );
}

async function getUpcomingMeetings(
  maxEvents: number,
): Promise<WebEvent[] | null> {
  const events = await getWebEvents();
  if (events === null) {
    console.error("Failed to load web events. Returning null.");
    return null;
  }

  return (
    events
      .filter((event) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return event.type == EventType.MonthlyMeeting && event.endTime >= today;
      })
      .slice(0, maxEvents) || []
  );
}

async function getUpcomingTurnersTalks(
  maxEvents: number,
): Promise<WebEvent[] | null> {
  const events = await getWebEvents();
  if (events === null) {
    console.error("Failed to load web events. Returning null.");
    return null;
  }

  return (
    events
      .filter((event) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return event.type == EventType.TurnersTalk && event.endTime >= today;
      })
      .slice(0, maxEvents) || []
  );
}

export async function getNextMeeting(): Promise<WebEvent | null> {
  const meetings = await getUpcomingMeetings(1);
  return meetings !== null && meetings.length > 0 ? meetings[0] : null;
}

export async function getNextTurnersTalk(): Promise<WebEvent | null> {
  const turnersTalks = await getUpcomingTurnersTalks(1);
  return turnersTalks !== null && turnersTalks.length > 0
    ? turnersTalks[0]
    : null;
}

export function fillNextChallenge(selector: string): void {
  getUpcomingChallenges(1).then((events) => {
    if (events === null || events.length === 0) {
      return;
    }

    const event = events[0];
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) return;

    const dateElement = container.querySelector<HTMLElement>(".date");
    if (dateElement) {
      dateElement.innerText = event.startTime.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (event.topic) {
      const topicElement = container.querySelector<HTMLElement>(".topic");
      if (topicElement) {
        topicElement.innerText = event.topic;
      }
    }
  });
}

export function fillNextMeeting(selector: string): void {
  getUpcomingMeetings(1).then((events) => {
    if (events === null || events.length === 0) {
      return;
    }

    const event = events[0];
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) return;

    const dateElement = container.querySelector<HTMLElement>(".date");
    if (dateElement) {
      dateElement.innerText = event.startTime.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (event.presenter) {
      const presenterElement =
        container.querySelector<HTMLElement>(".presenter");
      if (presenterElement) {
        presenterElement.innerText = event.presenter;
      }
    }

    if (event.topic) {
      const topicElement = container.querySelector<HTMLElement>(".topic");
      if (topicElement) {
        topicElement.innerText = event.topic;
      }
    }
  });
}

export function fillNextTurnersTalk(selector: string): void {
  getUpcomingTurnersTalks(1).then((events) => {
    if (events === null || events.length === 0) {
      return;
    }

    const event = events[0];
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) return;

    const dateElement = container.querySelector<HTMLElement>(".date");
    if (dateElement) {
      dateElement.innerText = event.startTime.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    if (event.presenter) {
      const presenterElement =
        container.querySelector<HTMLElement>(".presenter");
      if (presenterElement) {
        presenterElement.innerText = event.presenter;
      }
    }

    if (event.topic) {
      const topicElement = container.querySelector<HTMLElement>(".topic");
      if (topicElement) {
        topicElement.innerText = event.topic;
      }
    }
  });
}

export function fillUpcomingChallenges(
  sectionId: string,
  count?: number,
  showHeader?: boolean,
): void {
  getUpcomingChallenges(count || 4).then((challenges) => {
    if (challenges === null || challenges.length === 0) {
      return;
    }

    const html = challenges
      .map((challenge) => {
        return `
                <div>
                    ${showHeader === false ? "" : `<h3>${challenge.topic}</h3>`}
                    <p><strong>${challenge.startTime.toLocaleDateString(
                      "en-US",
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      },
                    )}&mdash;</strong>
                    ${challenge.description}</p>
                </div>
            `;
      })
      .join("");

    const section = document.getElementById(sectionId);
    if (section) {
      section.innerHTML = html;
      if (section.classList.contains("expand-in")) {
        section.classList.add("visible");
      }
    }
  });
}

export function fillUpcomingMeetingEvents(sectionId: string): void {
  const section = document.getElementById(sectionId);
  const count = section?.getAttribute("data-count");

  getUpcomingMeetings(count ? parseInt(count) : 3).then((events) => {
    if (events === null || events.length === 0) {
      return;
    }

    const html = events
      .map((event) => {
        return `
                <div>
                  <p>
                    <strong>${event.startTime.toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}&mdash;</strong>
                    ${[event.topic, event.presenter].join(" with ")}
                  </p>
                </div>
            `;
      })
      .join("");

    if (section) {
      section.innerHTML = html;
      if (section.classList.contains("expand-in")) {
        section.classList.add("visible");
      }
    }
  });
}

export function fillUpcomingTurnersTalks(sectionId: string): void {
  const section = document.getElementById(sectionId);
  const count = section?.getAttribute("data-count");

  getUpcomingTurnersTalks(count ? parseInt(count) : 3).then((events) => {
    if (events === null || events.length === 0) {
      return;
    }

    const html = events
      .map((event) => {
        return `
                <div>
                  <p>
                    <strong>${event.startTime.toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}&mdash;</strong>
                    ${[event.topic, event.presenter].filter(Boolean).join(" facilitated by ")}
                  </p>
                </div>
            `;
      })
      .join("");

    if (section) {
      section.innerHTML = html;
      if (section.classList.contains("expand-in")) {
        section.classList.add("visible");
      }
    }
  });
}
